/**
 * Single source of truth for schedule/event phase.
 *
 * All UI consumers should read from this hook instead of computing
 * their own LIVE / countdown / ended logic.
 */
import React from 'react';
import { useNextEventForSchedule } from './useNextEventForSchedule';
import { isVideoReadyFromResults } from 'features/realtime/liveFromResults';
import { useServerNow } from 'shared/queries/serverTime';
import {
  isExecutionEndedForEvent,
  subscribeExecutionEnded,
  getExecutionEndedSnapshot,
} from 'features/events/stores/executionEndedStore';
import { computeEventPhase } from './computeEventPhase';

export type SchedulePhase = 'idle' | 'countdown' | 'starting' | 'live' | 'ended';

export interface ScheduleStatus {
  phase: SchedulePhase;
  eventId: string | number | null;
  eventTimestamp: number | null;
  countdownSeconds: number | null;
  endTimestamp: number | null;
  ongoingPayload: Record<string, unknown> | null;
  vodReady: boolean;
  nextEvent: any | null;
  refetchOngoing: () => void;
  notEndedEvents: any[];
}

export function useScheduleStatus(
  scheduleId: string | number | null | undefined,
  sportId?: string | number | null
): ScheduleStatus {
  const nextEventData = useNextEventForSchedule(scheduleId, sportId);
  const nowMs = useServerNow();
  const nowSec = Math.floor(Number(nowMs || 0) / 1000);

  const {
    nextEvent,
    eventId,
    eventTimestamp,
    endTimestamp,
    isLive,
    ongoingPayload,
    refetchOngoing,
    notEndedEvents,
  } = nextEventData;

  const vodReady = ongoingPayload != null
    ? isVideoReadyFromResults(ongoingPayload, sportId ?? null)
    : false;

  const useSES = (React as any).useSyncExternalStore as
    | undefined
    | ((sub: (cb: () => void) => () => void, get: () => number, getServer?: () => number) => number);
  const executionEndedSnapshot = useSES
    ? useSES(subscribeExecutionEnded, getExecutionEndedSnapshot, getExecutionEndedSnapshot)
    : 0;

  const phase: SchedulePhase = React.useMemo(() => {
    if (scheduleId == null || eventId == null) return 'idle';

    if (isExecutionEndedForEvent(eventId)) return 'ended';

    if (isLive) return 'live';

    if (eventTimestamp != null && Number.isFinite(eventTimestamp) && nowSec >= eventTimestamp) {
      if (endTimestamp != null && Number.isFinite(endTimestamp) && nowSec >= endTimestamp) {
        return 'ended';
      }
      return 'starting';
    }

    if (eventTimestamp != null && Number.isFinite(eventTimestamp) && nowSec < eventTimestamp) {
      return 'countdown';
    }

    return 'idle';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, eventId, isLive, eventTimestamp, endTimestamp, nowSec, executionEndedSnapshot]);

  const countdownSeconds = React.useMemo(() => {
    if (phase === 'live' || phase === 'starting') return 0;
    if (phase === 'ended' || phase === 'idle') return null;
    if (eventTimestamp == null || !Number.isFinite(eventTimestamp)) return null;
    return Math.max(0, eventTimestamp - nowSec);
  }, [phase, eventTimestamp, nowSec]);

  return {
    phase,
    eventId,
    eventTimestamp,
    countdownSeconds,
    endTimestamp,
    ongoingPayload,
    vodReady,
    nextEvent,
    refetchOngoing,
    notEndedEvents,
  };
}

export { computeEventPhase } from './computeEventPhase';
